import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useStores } from "../../../hooks/useStores";

type PasswordChangeDialogProps = {
  open: boolean;
  onClose: () => void;
  onFinish: (message: string) => void;
};

export function PasswordChangeDialog({ open, onClose, onFinish }: PasswordChangeDialogProps) {
  const { authStore } = useStores();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isCurrentPasswordConfirmed, setIsCurrentPasswordConfirmed] = useState(false);
  const [isCurrentPasswordInputCorrect, setIsCurrentPasswordInputCorrect] = useState<boolean | undefined>(undefined);

  const [isNewPasswordValid, setINewPasswordValid] = useState<boolean | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(false);
  
  const validateCurrentPassword = async (value: string) => {
    const response = await authStore.verifyPassword(value);
    setIsCurrentPasswordInputCorrect(response);
    
    if (response === true) {
      setIsCurrentPasswordConfirmed(true);
    } else {
      setIsCurrentPasswordConfirmed(false);
    }
  };

  const isNewPasswordError = (() => {
    if (isNewPasswordValid === false) {
      return true;
    }

    if (isNewPasswordValid) {
      if (currentPassword === newPassword) {
        return true;
      }
    }
    
    return false;
  })();

  const isNewPasswordHelperText = (() => {
    if (isNewPasswordValid === false) {
      return "비밀번호는 영문, 숫자, 특수문자 중 2가지 이상을 조합하여 6자 ~ 14자로 입력해 주세요.";
    } 
    
    if (isNewPasswordValid) {
      if (currentPassword === newPassword) {
        return "현재 사용 중인 비밀번호입니다.";
      }
    }

    return "";
  })();

  const onUpdatePassword = async () => {
    setIsLoading(true);
    const response = await authStore.simplyUpdatePasswordOnly(newPassword);

    setIsLoading(false);

    if (response) {
      onFinish('비밀번호가 변경되었습니다.');
      onGoingToCallOnClose();
    }
  };

  const onGoingToCallOnClose = () => {
    // clean up
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setIsCurrentPasswordConfirmed(false);
    setIsCurrentPasswordInputCorrect(undefined);
    setINewPasswordValid(undefined);
    setIsLoading(false);

    onClose();
  };

  return (
    <Dialog open={open} onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
              onClose();
          }
      }} 
      maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: 20, textAlign: "center" }}>
        비밀번호 변경
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 1 }}>
        {isLoading && <CircularProgress sx={{ mb: 2 }} />}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 0.5 }}>현재 비밀번호</Typography>
          <TextField
            fullWidth
            type="password"
            value={currentPassword}
            onChange={(e) => {
              setIsCurrentPasswordConfirmed(false);
              setCurrentPassword(e.target.value);
            }}
            onBlur={() => {
              validateCurrentPassword(currentPassword);
            }}
            error={isCurrentPasswordInputCorrect === false}
            helperText={isCurrentPasswordInputCorrect === false ? "잘못된 비밀번호입니다." : ""}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 0.5 }}>새 비밀번호</Typography>
          <TextField
            fullWidth
            type="password"
            value={newPassword}
            onChange={(e) => {
              const result = authStore.passwordValidation(e.target.value);
              if (result[1] === false) {
                setINewPasswordValid(false);
              } else {
                setINewPasswordValid(true);
              }
              setNewPassword(e.target.value);
            }}
            error={isNewPasswordError}
            helperText={isNewPasswordHelperText}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 0.5 }}>새 비밀번호 확인</Typography>
          <TextField
            fullWidth
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword && newPassword !== confirmPassword}
            helperText={
              confirmPassword && newPassword !== confirmPassword
                ? "비밀번호가 일치하지 않습니다."
                : ""
            }
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          • 영문, 숫자, 특수문자 중 2가지 이상을 조합하여 6자~14자로 입력해 주세요.
          <br />
          • 현재 사용 중인 비밀번호는 사용할 수 없습니다.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, pt: 2, justifyContent: "space-between" }}>
        <Button
          variant="contained"
          disabled={!isCurrentPasswordConfirmed || (isNewPasswordValid !== true) || (newPassword !== confirmPassword) || (currentPassword === newPassword) || (isLoading === true)}
          sx={{
            backgroundColor: "#008064",
            width: "48%",
            height: 45,
            fontWeight: "bold",
            fontSize: 16,
            ":hover": { backgroundColor: "#006a52" },
          }}
          onClick={onUpdatePassword}
        >
          비밀번호 변경하기
        </Button>
        <Button
          onClick={onGoingToCallOnClose}
          variant="contained"
          disabled={(isLoading === true)}
          sx={{
            backgroundColor: "#666",
            width: "48%",
            height: 45,
            fontWeight: "bold",
            fontSize: 16,
            ":hover": { backgroundColor: "#555" },
          }}
        >
          다음에 변경하기
        </Button>
      </DialogActions>
    </Dialog>
  );
}
